<script setup lang="ts">
import {ref, computed, watch, inject, onUnmounted} from 'vue';
import {useLocalStorage, refDebounced, useStyleTag} from '@vueuse/core';
import DiffFilter from '../../../components/AllSpiceDiff/DiffFilter.vue';
import {determineSchemaType, makeDiffFilterTargetId} from '../../../components/AllSpiceDiff/diff-filter-shared.ts';
import {AllSpiceJsonLoader, SCHEMA_TYPES, type SchemaType} from '../../../features/allspicejson.ts';
import {SvgIcon} from '../../../svg.ts';
import DiffItem from './DiffItem.vue';
import VirtualDiffList from './VirtualDiffList.vue';
import {buildTree, InspectorContext, headOrBase} from '../../../components/inspector_tree.ts';
import type {TreeNode} from '../../../components/inspector_tree.ts';
import {buildDiffTree, normalizeDiffTreeData, type DiffJson} from '../../../components/diff_json.ts';
import {buildDiffPreviewRoute} from '../../../components/AllSpiceDiff/AllSpiceDiffViewRouteBuilder.ts';
import type {InspectorDiffTreeState} from '../../../components/inspector_diff_tree.ts';
import {FetchRetryer} from '../../../fetchretryer.ts';
import {type PreviewableTreeNode, type FileDiffStatus, FILE_DIFF_STATUS} from '../../types/file_tree.ts';
import {formatGroupName, formatNodeInfo, groupChanges, makePageNameMap} from './diff-grouping.ts';

import type {Container} from '../../../container.ts';
import type {Store} from '../../store.ts';
import {isProjectPageDiff, type ProjectPageDiffPreview} from '../../types/previews.ts';

import {useLayersStore} from '../../stores/layers.ts';

import {useSidebarResize} from '../use-sidebar-resize.ts';

import {isProjectViewEnabled} from '../../../allspice.ts';
import {featureFlag} from '../../lib/feature-flag.ts';
import {determineVisibility, VISIBILITY} from './diff-item-visibility.ts';
import {
  isProjectPageStructuredDiffLoadable,
  scopeDiffJsonToPage,
  scopeHeadJsonToPage,
} from './diff-list-state.ts';
import {setDiffLayers} from '../../../components/AllSpiceDiff/diff-layer-utils.ts';
import {useDiffToggleStore, DIFF_TOGGLE_OPTIONS, type DiffToggleOption} from '../../stores/diffToggle.ts';
import {saveDiffFilterState} from '../../diffFilterPersistence.ts';
import {useZoomLockStore} from '../../stores/zoomLock.ts';

const enablePcbDiffList = featureFlag('enablePcbDiffList');

const isExpandedByDefault = useLocalStorage('right-sidebar-expanded', true);
const controlsMovedDismissed = useLocalStorage('rhp-controls-moved-dismissed', false);
const wasExpandedByUser = ref<boolean>(false);
const isFilterVisible = ref<boolean>(false);
const searchQuery = ref<string>('');
const debouncedSearchQuery = refDebounced(searchQuery, 300);
const isDiffDropdownOpen = ref<boolean>(false);
const diffFilterRef = ref<InstanceType<typeof DiffFilter> | null>(null);

const diffToggleStore = useDiffToggleStore();
const zoomLockStore = useZoomLockStore();

// CSS-based diff layer visibility ensures layers are hidden the instant SVG
// elements enter the DOM, avoiding race conditions with JS-based setDiffLayers
// which may run before the SVG is fully rendered.
const diffToggleCss = computed(() => {
  switch (diffToggleStore.selectedKey) {
    case 'added':
      return '.diff.diff_from { display: none !important; }';
    case 'removed':
      return '.diff.diff_to { display: none !important; }';
    case 'no':
      return '.diff.diff_from, .diff.diff_to, .diff-backdrop { display: none !important; }';
    default:
      return '';
  }
});
useStyleTag(diffToggleCss);

const title = 'Changes';
const titleIcon = 'octicon-file-diff';

const isLoading = ref<boolean>(false);
const requestedDiffStateByHash = ref<Record<string, boolean>>({});

const baseJson = ref<Record<string, unknown> | null>(null);
const headJson = ref<Record<string, unknown> | null>(null);
const diffJson = ref<DiffJson | null>(null);

type LoadedDiffTreeState = {
  headJson: Record<string, unknown> | null;
  baseJson: Record<string, unknown> | null;
  diffJson: DiffJson | null;
};

const DIFF_FILE_TYPES = {
  schematic: 'sch-diff',
  pcb: 'pcb-diff',
  multiPage: 'multi-page-diff',
  projectPage: 'proj-page-diff',
} as const;

type DiffFileType = typeof DIFF_FILE_TYPES[keyof typeof DIFF_FILE_TYPES];

type ActiveDiffFileInfo = {
  fileType: PreviewableTreeNode['ty'];
  basePath: string;
  headPath: string;
  status: FileDiffStatus;
  pageId?: string;
};

const {sidebarWidth, handleStartResize} = useSidebarResize('right');

const store = inject<Store>('store');
const container: Container = inject('container');
const inspectorDiffTreeState = inject<InspectorDiffTreeState>('inspectorDiffTreeState');
const layersStore = useLayersStore();

const validFileTypes = [
  DIFF_FILE_TYPES.schematic,
  enablePcbDiffList ? DIFF_FILE_TYPES.pcb : null,
  DIFF_FILE_TYPES.multiPage,
  DIFF_FILE_TYPES.projectPage,
].filter((fileType): fileType is DiffFileType => fileType !== null);

// Restore persisted toggle state immediately on setup (before any navigation).
// This ensures the Pinia store reflects the saved state even if the URL has
// no hash (activePath stays '' and the watcher below never fires).
const savedToggle = store.diffFilterConfig?.diffToggle.value;
if (savedToggle && savedToggle !== 'full') {
  diffToggleStore.select(savedToggle);
}

// Note: we intentionally do NOT reset the diff toggle when navigating between
// files. The toggle selection persists across navigation. When a new SVG loads,
// EcadDocument.enableDiffToggle() calls refreshDiffLayers() which reads the
// Pinia store and applies the correct layer visibility.

function setLoadedDiffTreeState(nextState: LoadedDiffTreeState): void {
  headJson.value = nextState.headJson;
  baseJson.value = nextState.baseJson;
  diffJson.value = nextState.diffJson;
}

function clearLoadedDiffTreeState(): void {
  setLoadedDiffTreeState({
    headJson: null,
    baseJson: null,
    diffJson: null,
  });
}

function resetHiddenDiffFilterIds(): void {
  store.diffFilterHiddenIds.value = [];
}

function getActiveDiffFileInfo(node: PreviewableTreeNode | undefined): ActiveDiffFileInfo | null {
  if (!node || !('basePath' in node) || !('headPath' in node)) {
    return null;
  }

  return {
    fileType: node.ty,
    basePath: node.basePath,
    headPath: node.headPath,
    status: node.status,
    pageId: 'pageId' in node ? node.pageId : undefined,
  };
}

function isDiffFileType(fileType: PreviewableTreeNode['ty']): fileType is DiffFileType {
  return validFileTypes.includes(fileType as DiffFileType);
}

function isLoadableFileDiff(fileInfo: ActiveDiffFileInfo): boolean {
  if (store.activePreview.value.ty === 'proj-page-diff') {
    return fileInfo.fileType === DIFF_FILE_TYPES.projectPage && isProjectPageStructuredDiffLoadable(fileInfo.status);
  }

  return store.activePreview.value.ty === 'file-diff' && isDiffFileType(fileInfo.fileType) && fileInfo.fileType !== DIFF_FILE_TYPES.projectPage;
}

function ensureActiveDiffStateRequested(): void {
  const activeHash = store.activeHash.value;
  if (!activeHash || requestedDiffStateByHash.value[activeHash]) return;
  requestedDiffStateByHash.value = {
    ...requestedDiffStateByHash.value,
    [activeHash]: true,
  };
}

function getProjectPageDiffTreeState(preview: ProjectPageDiffPreview): LoadedDiffTreeState {
  return {
    headJson: preview.pageComponent?.head,
    baseJson: preview.pageComponent?.base,
    diffJson: null,
  };
}

async function loadFileDiffStateForPreview(fileInfo: ActiveDiffFileInfo, isStale: () => boolean): Promise<boolean> {
  if (!isLoadableFileDiff(fileInfo)) {
    return false;
  }

  isLoading.value = true;

  try {
    const nextState = await loadDiffTreeState(
      store.diffInfo.username,
      store.diffInfo.reponame,
      fileInfo.basePath,
      store.diffInfo.baseCommit,
      fileInfo.headPath,
      store.diffInfo.headCommit,
      fileInfo.pageId,
    );

    if (!isStale()) {
      setLoadedDiffTreeState(nextState);
    }
  } finally {
    if (!isStale()) {
      isLoading.value = false;
    }
  }

  return true;
}

const fileStatus = computed<FileDiffStatus>(() => {
  const file = store.hashToPathMap.value.get(store.activeHash.value);

  return file?.status ?? FILE_DIFF_STATUS.UNCHANGED;
});

async function loadDiffTreeState(
  repoOwner: string,
  repoName: string,
  baseFilePath: string,
  baseCommit: string,
  headFilePath: string,
  headCommit: string,
  activePageId?: string,
): Promise<LoadedDiffTreeState> {
  const status = fileStatus.value;
  if (status === FILE_DIFF_STATUS.ADDED || status === FILE_DIFF_STATUS.DELETED) {
    return {
      headJson: null,
      baseJson: null,
      diffJson: null,
    };
  }

  const headUrl = AllSpiceJsonLoader.buildUrl(container, repoOwner, repoName, headCommit, headFilePath);
  const diffListUrl = buildDiffPreviewRoute(
    container, repoOwner, repoName, 'diff_list', headCommit, headFilePath,
    '', '', baseCommit, baseFilePath,
  );

  try {
    const headFetch = FetchRetryer.for(container, headUrl, {});
    const diffFetch = FetchRetryer.for(container, diffListUrl, {});

    headFetch.startFetching();
    diffFetch.startFetching();

    const [headResult, diffResult] = await Promise.all([headFetch.promise, diffFetch.promise]);

    if (!headResult.response || !diffResult.response) {
      throw new Error('Failed to load head or diff list JSON');
    }

    // .clone() is required: FetchRetryer caches Response objects per-URL via
    // container.cacheFor, so other consumers (e.g. fetchProjectPageDiffPreview
    // in loader.ts) share this Response and need to read its body too.
    const rawHeadJson = await headResult.response.clone().json();
    const rawDiffListJson = await diffResult.response.clone().json();
    const standardizedHeadJson = AllSpiceJsonLoader.standardizeJson(rawHeadJson);
    const normalizedDiffTreeData = normalizeDiffTreeData(standardizedHeadJson, rawDiffListJson);

    // Trim multi-page payloads to the active page before buildDiffTree walks
    // them — the walker materializes a TreeNode for every leaf, so leaving
    // other pages in causes O(file) work per page click on large schematics.
    return {
      headJson: scopeHeadJsonToPage(normalizedDiffTreeData.headJson, activePageId),
      baseJson: null,
      diffJson: scopeDiffJsonToPage(normalizedDiffTreeData.diffJson, activePageId),
    };
  } catch (err) {
    console.error('Failed to load diff data:', err);
    return {
      headJson: null,
      baseJson: null,
      diffJson: null,
    };
  }
}

const showChangesForThisFile = computed(() => {
  const file = store.hashToPathMap.value.get(store.activeHash.value);

  if (!file || !isDiffFileType(file.ty)) return false;
  if (file.ty === 'proj-page-diff') return true;
  return file.status === FILE_DIFF_STATUS.MODIFIED || file.status === FILE_DIFF_STATUS.RENAMED;
});

const schemaType = computed<SchemaType>(() => {
  return determineSchemaType(headJson.value, baseJson.value ?? null);
});

// Show "controls moved here" indicator when toolbar controls are hidden
// (project view + schematic, not PCB) and user hasn't dismissed it yet.
const showControlsMovedIndicator = computed(() => {
  return !controlsMovedDismissed.value && isProjectViewEnabled() && showChangesForThisFile.value && schemaType.value !== SCHEMA_TYPES.PCB;
});

// Mark as dismissed once the banner has been rendered, so it won't show
// on the next page load. The banner stays visible for this session until
// the user navigates away or closes it.
watch(showControlsMovedIndicator, (visible) => {
  if (visible) {
    controlsMovedDismissed.value = true;
  }
});

const isSidebarExpanded = computed(() => {
  // if the user expands a by-default-collapsed sidebar, keep it expanded
  // otherwise, expand or collapse by default based on whether there's something to show and what the user has done previously
  // also force-expand when controls have moved here so the user sees them
  return wasExpandedByUser.value || (showChangesForThisFile.value && isExpandedByDefault.value) || showControlsMovedIndicator.value;
});

function handleSidebarExpandClick(expand: boolean) {
  isExpandedByDefault.value = expand;
  wasExpandedByUser.value = expand;
  if (expand) {
    ensureActiveDiffStateRequested();
  }
  // Collapsing the sidebar counts as dismissing the "controls moved" banner
  if (!expand) {
    controlsMovedDismissed.value = true;
  }
}

function hasLoadedDiffTreeState(): boolean {
  return headJson.value !== null || baseJson.value !== null || diffJson.value !== null;
}

let activeDiffTreeSyncGeneration = 0;

function createActiveDiffTreeSyncStaleGuard(onCleanup: (cleanupFn: () => void) => void): () => boolean {
  const generation = ++activeDiffTreeSyncGeneration;
  let cleanedUp = false;

  onCleanup(() => {
    cleanedUp = true;
  });

  return () => cleanedUp || generation !== activeDiffTreeSyncGeneration;
}

async function syncActiveDiffTreeState(isStale: () => boolean): Promise<void> {
  const fileInfo = getActiveDiffFileInfo(store.hashToPathMap.value.get(store.activeHash.value));
  if (!fileInfo) {
    clearLoadedDiffTreeState();
    isLoading.value = false;
    return;
  }

  const activeHash = store.activeHash.value;
  const hasRequestedState = activeHash ? requestedDiffStateByHash.value[activeHash] : false;
  const isLoadable = isLoadableFileDiff(fileInfo);

  if (!hasRequestedState && isLoadable) {
    if (isSidebarExpanded.value) {
      ensureActiveDiffStateRequested();
    } else {
      clearLoadedDiffTreeState();
      isLoading.value = false;
      return;
    }
  }

  if (await loadFileDiffStateForPreview(fileInfo, isStale)) {
    return;
  }

  if (isStale()) {
    return;
  }

  const activePreview = store.activePreview.value;
  if (isProjectPageDiff(activePreview)) {
    const state = getProjectPageDiffTreeState(activePreview);
    setLoadedDiffTreeState(state);
    isLoading.value = false;
    return;
  }

  clearLoadedDiffTreeState();
  isLoading.value = store.activePreview.value.ty === 'loading';
}

watch(() => ({path: store.activePath.value, ty: store.activePreview.value.ty}), async (_currentValue, _previousValue, onCleanup) => {
  resetHiddenDiffFilterIds();

  const isStale = createActiveDiffTreeSyncStaleGuard(onCleanup);
  await syncActiveDiffTreeState(isStale);
});

watch(isSidebarExpanded, async (expanded, previousExpanded, onCleanup) => {
  if (!expanded || previousExpanded) {
    return;
  }

  if (hasLoadedDiffTreeState() || isLoading.value) {
    return;
  }

  const isStale = createActiveDiffTreeSyncStaleGuard(onCleanup);
  await syncActiveDiffTreeState(isStale);
});

function handleDiffToggleSelect(option: DiffToggleOption) {
  diffToggleStore.select(option.key);
  isDiffDropdownOpen.value = false;

  // document-level scope is intentional: the sidebar lives outside .diff-file-box,
  // and the Vue app only shows one active diff file at a time.
  setDiffLayers(document, option.showAddedChanged, option.showRemoved);

  // Persist the toggle state
  if (store.diffFilterConfig) {
    store.diffFilterConfig.diffToggle.value = option.key;
    saveDiffFilterState(store);
  }
}

function closeDiffDropdown() {
  isDiffDropdownOpen.value = false;
}

function resetAllFilters() {
  // Reset diff toggle to Full Diff
  diffToggleStore.select('full');
  setDiffLayers(document, true, true);

  // Reset diff filter (select all groups)
  diffFilterRef.value?.onSelectAll(new MouseEvent('click'));

  // Clear search
  searchQuery.value = '';

  // Clear stored overrides globally so other files don't re-apply stale state
  if (store.diffFilterConfig) {
    store.diffFilterConfig.diffToggle.value = 'full';
    store.diffFilterConfig.filterOverrides.value = {};
    saveDiffFilterState(store);
  }
  // Clear hidden IDs directly so the banner hides immediately
  store.diffFilterHiddenIds.value = [];
}

watch(isDiffDropdownOpen, (open) => {
  if (open) {
    document.addEventListener('click', closeDiffDropdown);
  } else {
    document.removeEventListener('click', closeDiffDropdown);
  }
});

// Fix #3: clean up document click handler if component unmounts while dropdown is open
onUnmounted(() => {
  document.removeEventListener('click', closeDiffDropdown);
  inspectorDiffTreeState?.clearActiveInspectorDiffTree();
});

const metadataCtx = computed(() => {
  return new InspectorContext({
    window,
    treePath: store.activePath.value,
    repoOwner: store.diffInfo.username,
    repoName: store.diffInfo.reponame,
    commitSHA: store.diffInfo.headCommit,
    layers: (headOrBase(headJson.value, baseJson.value, 'layers', {}) ?? {}) as Record<number, {id: number, name: string}>,
    externalDesignBlocks: {},
    projectDocuments: {},
    projectPath: '',
    variants: {},
  });
});

const diffTree = computed(() => {
  if (diffJson.value && headJson.value) {
    return buildDiffTree(
      {headJson: headJson.value, diffJson: diffJson.value},
      metadataCtx.value,
      {units: 'mm', includeUnchangedEntities: false},
    );
  }
  // Fallback for proj-page-diff which provides head + base directly
  return buildTree(
    '', headJson.value, baseJson.value, 0,
    false, false, true, 'REPLACE', false, null, metadataCtx.value,
  );
});

const activeInspectorDiffTree = computed(() => {
  if (!showChangesForThisFile.value) {
    return null;
  }

  if (headJson.value === null && baseJson.value === null) {
    return null;
  }

  return diffTree.value;
});

watch(activeInspectorDiffTree, (nextTree) => {
  inspectorDiffTreeState?.setActiveInspectorDiffTree(nextTree);
  inspectorDiffTreeState?.setRawDiffJson(diffJson.value);
}, {immediate: true});

const groupedChanges = computed(() => {
  const groups = groupChanges(schemaType.value, diffTree.value);
  // Return null instead of {} so DiffFilter doesn't treat an empty object as
  // truthy and skip the JSON-parsing fallback path before data has loaded.
  return Object.keys(groups).length > 0 ? groups : null;
});

const pageNameMap = computed(() => {
  return makePageNameMap(diffTree.value);
});

// Visibility predicate for a single change, excluding the DiffFilter's own
// hidden-IDs list.  Used by both filteredChanges and groupCountsForFilter so
// that the filter panel's per-group counts stay consistent with the diff list.
function isChangeVisible(change: TreeNode, groupName: string): boolean {
  return (
    // diff toggle
    (
      (((change.added || change.modified) && !change.removed) && diffToggleStore.showAddedChanged) ||
      (change.removed && diffToggleStore.showRemoved)
    ) &&
    // layer toggle (only applies to PCB files)
    (
      (!layersStore.hasLayers) ||
      (schemaType.value !== SCHEMA_TYPES.PCB) ||
      determineVisibility(layersStore.visibleLayerIds, change, groupName, schemaType.value, diffTree.value) !== VISIBILITY.NONE
    ) &&
    // search
    (
      debouncedSearchQuery.value === '' ||
      (formatNodeInfo(schemaType.value, change, groupName).name ?? change.key).toLowerCase().includes(debouncedSearchQuery.value.toLowerCase())
    )
  );
}

const navigationRootId = computed(() => {
  return store.activeHash.value ? `diff-${store.activeHash.value}` : undefined;
});

const filteredChanges = computed(() => {
  const unfiltered = groupedChanges.value;
  if (!unfiltered) return {};
  const filtered: typeof groupedChanges.value = {};

  for (const groupName of Object.keys(unfiltered)) {
    filtered[groupName] = unfiltered[groupName].filter((change) => (
      !store.diffFilterHiddenIds.value.includes(makeDiffFilterTargetId(change.page, change.key)) &&
      isChangeVisible(change, groupName)
    ));

    if (filtered[groupName].length === 0) {
      delete filtered[groupName];
    }
  }

  return filtered;
});

// Per-group counts applying all filters EXCEPT DiffFilter's hidden IDs.
// Used as the "total available" count (right number in "24/38").
const groupCountsForFilter = computed(() => {
  const unfiltered = groupedChanges.value;
  if (!unfiltered) return null;
  const counts: Record<string, number> = {};
  for (const groupName of Object.keys(unfiltered)) {
    counts[groupName] = unfiltered[groupName].filter(
      (change) => isChangeVisible(change, groupName),
    ).length;
  }
  return counts;
});

// Per-group counts applying ALL filters including DiffFilter's hidden IDs.
// Used as the "currently visible" count (left number in "24/38").
const visibleGroupCounts = computed(() => {
  const counts: Record<string, number> = {};
  for (const [groupName, changes] of Object.entries(filteredChanges.value)) {
    counts[groupName] = changes.length;
  }
  return counts;
});

const numberOfChanges = computed(() => {
  if (!groupedChanges.value) return 0;
  let count = 0;
  for (const changes of Object.values(groupedChanges.value)) {
    count += changes.length;
  }
  return count;
});

const numberOfFilteredChanges = computed(() => {
  if (!filteredChanges.value) return 0;
  let count = 0;
  for (const changes of Object.values(filteredChanges.value)) {
    count += changes.length;
  }
  return count;
});

const titleWithChanges = computed(() => {
  if (showChangesForThisFile.value && hasLoadedDiffTreeState() && !isLoading.value) {
    if (numberOfChanges.value !== numberOfFilteredChanges.value) {
      return `${title} (${numberOfFilteredChanges.value}/${numberOfChanges.value})`;
    }
    return `${title} (${numberOfChanges.value})`;
  }
  return title;
});
</script>

<template>
  <div
    v-if="isSidebarExpanded"
    :style="{ maxWidth: sidebarWidth + 'px' }"
    class="tw:sticky tw:flex tw:max-h-[calc(100vh-47px)] tw:top-[47px] tw:w-full"
    @focusin="ensureActiveDiffStateRequested"
  >
    <div
      class="resize-handle tw:cursor-col-resize tw:h-full tw:w-3 tw:shrink-0"
      @mousedown="handleStartResize"
    />
    <div class="diff-list tw:w-full tw:h-full tw:flex tw:flex-col tw:overflow-hidden">
      <div
        class="tw:p-2 tw:w-full tw:flex tw:gap-1 tw:font-semibold tw:cursor-pointer"
        @click="handleSidebarExpandClick(false)"
      >
        <button
          class="diff-toggle-changes-list-button btn interact-fg"
        >
          <SvgIcon
            :name="titleIcon"
            :size="20"
            class="icon"
          />
        </button>
        <span>{{ titleWithChanges }}</span>
        <div
          class="tw:ml-auto tw:flex tw:gap-1"
          @click.stop
        >
          <div
            v-if="showChangesForThisFile"
            class="tw:relative"
          >
            <button
              class="btn interact-fg tw:p-1"
              data-tooltip-content="Toggle diff overlay"
              @click="isDiffDropdownOpen = !isDiffDropdownOpen"
            >
              <SvgIcon
                :name="diffToggleStore.isDiffVisible ? 'octicon-eye' : 'octicon-eye-closed'"
                :size="16"
              />
              <span
                v-if="diffToggleStore.selectedKey !== 'full'"
                class="tw:inline-block tw:w-2 tw:h-2 tw:rounded-full tw:bg-[var(--color-accent)] tw:ml-0.5"
              />
            </button>
            <div
              v-if="isDiffDropdownOpen"
              class="tw:absolute tw:right-0 tw:top-full tw:mt-1 tw:border tw:border-solid tw:border-secondary tw:rounded tw:shadow-lg tw:z-50 tw:min-w-[160px] tw:py-1 tw:bg-box-body"
            >
              <button
                v-for="option in DIFF_TOGGLE_OPTIONS"
                :key="option.key"
                class="tw:block tw:w-full tw:text-left tw:px-3 tw:py-1.5 tw:text-sm tw:bg-transparent tw:border-0 tw:hover:bg-hover"
                :class="{'tw:font-bold tw:text-[var(--color-accent)] diff-toggle-selected': diffToggleStore.selectedKey === option.key}"
                @click="handleDiffToggleSelect(option)"
              >
                {{ option.label }}
              </button>
            </div>
          </div>
          <button
            v-if="showChangesForThisFile"
            class="btn interact-fg tw:p-1"
            :class="{'tw:text-[var(--color-accent)]': isFilterVisible}"
            data-tooltip-content="Filter by group"
            @click="isFilterVisible = !isFilterVisible"
          >
            <SvgIcon
              name="octicon-filter"
              :size="16"
            />
            <span
              v-if="store.diffFilterHiddenIds.value.length > 0"
              class="tw:inline-block tw:w-2 tw:h-2 tw:rounded-full tw:bg-[var(--color-accent)] tw:ml-0.5"
            />
          </button>
          <button
            v-if="showChangesForThisFile"
            class="btn interact-fg tw:p-1 zoom-lock-toggle"
            :class="{'tw:text-[var(--color-accent)]': zoomLockStore.isLocked}"
            :aria-pressed="zoomLockStore.isLocked"
            data-tooltip-content="Lock zoom level when jumping to a change or search result"
            @click="zoomLockStore.toggle()"
          >
            <SvgIcon
              :name="zoomLockStore.isLocked ? 'octicon-lock' : 'octicon-unlock'"
              :size="16"
            />
          </button>
        </div>
      </div>
      <div
        v-if="showChangesForThisFile"
        class="tw:px-2 tw:pb-2"
      >
        <input
          v-model="searchQuery"
          type="text"
          class="tw:w-full tw:px-2 tw:py-1 tw:border tw:border-solid tw:border-secondary tw:rounded tw:text-sm"
          placeholder="Search..."
          @focus="ensureActiveDiffStateRequested"
          @click.stop
        >
      </div>
      <div
        v-if="showControlsMovedIndicator"
        class="
          controls-moved-banner
          tw:m-1 tw:p-2 tw:rounded tw:text-sm
          tw:border tw:border-solid tw:border-info-border
          tw:bg-info-bg tw:color-info-color
          tw:flex tw:gap-2 tw:items-center
        "
      >
        <SvgIcon
          name="octicon-info"
          :size="14"
        />
        <span class="tw:grow">The Diff Toggle and Diff Filter have moved from the toolbar to this panel. You can also use the new search to find schematic elements.</span>
        <button
          class="btn tw:p-0.5"
          @click.stop="controlsMovedDismissed = true"
        >
          <SvgIcon
            name="octicon-x"
            :size="14"
          />
        </button>
      </div>
      <DiffFilter
        v-if="showChangesForThisFile"
        v-show="isFilterVisible"
        ref="diffFilterRef"
        :key="store.activeHash.value"
        :head-json="headJson"
        :base-json="baseJson"
        :grouped-changes="groupedChanges"
        :schema-type="schemaType"
        :page-name-map="pageNameMap"
        :filtered-group-counts="groupCountsForFilter"
        :visible-group-counts="visibleGroupCounts"
        :sidebar="true"
      />
      <div
        v-if="numberOfChanges !== numberOfFilteredChanges"
        class="
          diff-list-filtered-message
          tw:m-1 tw:border tw:border-solid tw:border-info-border tw:p-2 tw:rounded
          tw:bg-info-bg tw:color-info-color
          tw:flex tw:gap-2 tw:items-center
        "
      >
        <SvgIcon name="octicon-filter" />
        <div class="tw:flex-1">
          This list of changes has been filtered.
        </div>
        <button
          class="btn tw:text-sm tw:px-2 tw:py-0.5 tw:border tw:border-solid tw:border-secondary tw:rounded"
          @click.stop="resetAllFilters"
        >
          Reset
        </button>
      </div>
      <div
        v-if="isLoading"
        class="diff-list-is-loading tw:h-full tw:w-full tw:flex tw:flex-col tw:gap-2 tw:justify-center tw:items-center"
      >
        <div class="is-loading tw:size-8" />
        <div>Loading changes...</div>
      </div>
      <div
        v-else-if="!showChangesForThisFile"
        class="tw:h-full tw:w-full tw:flex tw:flex-col tw:gap-2 tw:justify-center tw:items-center"
      >
        <SvgIcon name="octicon-diff-ignored" />
        <div>Cannot show changes for this file type.</div>
      </div>
      <div
        v-else-if="!(diffJson || headJson) || numberOfFilteredChanges === 0"
        class="tw:h-full tw:w-full tw:flex tw:flex-col tw:gap-2 tw:justify-center tw:items-center"
      >
        <SvgIcon name="octicon-diff-ignored" />
        <div>No changes to show.</div>
      </div>
      <div
        v-else
        class="tw:overflow-y-auto tw:grow tw:min-h-0 tw:w-full"
      >
        <VirtualDiffList
          v-if="schemaType === SCHEMA_TYPES.PCB"
          :changes="filteredChanges"
          :schema-type="schemaType"
          :page-name-map="pageNameMap"
          :diff-tree="diffTree"
          :navigation-root-id="navigationRootId"
        />
        <template
          v-for="(changes, groupName) of filteredChanges"
          v-else
          :key="groupName"
        >
          <div class="tw:text-base tw:font-bold tw:ml-2">
            {{ formatGroupName(schemaType, groupName) }}
          </div>
          <DiffItem
            v-for="change of changes"
            :key="change.key"
            :item="change"
            :group-name="groupName"
            :page-name="pageNameMap[change.page]"
            :schema-type="schemaType"
            :diff-tree="diffTree"
            :navigation-root-id="navigationRootId"
          />
        </template>
      </div>
    </div>
  </div>
  <div
    v-else
    class="tw:cursor-pointer tw:flex tw:gap-2 tw:font-semibold tw:p-2 diff-sidebar-collapsed"
    @click="handleSidebarExpandClick(true)"
  >
    <button
      class="diff-toggle-changes-list-button btn interact-fg"
    >
      <SvgIcon
        :name="titleIcon"
        :size="20"
        class="icon"
      />
    </button>
    <span>{{ titleWithChanges }}</span>
  </div>
</template>

<style scoped>
  .resize-handle {
    border-left: 1px solid var(--color-secondary);
  }
  .resize-handle:hover {
    background: var(--color-hover);
  }

  .diff-sidebar-collapsed {
    writing-mode: vertical-rl;
  }

  .diff-toggle-selected {
    background-color: color-mix(in srgb, var(--color-primary) 10%, transparent) !important;
  }

  .controls-moved-banner {
    animation: controls-moved-pulse 1.2s ease-in-out 3;
  }

  @keyframes controls-moved-pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.4;
      transform: scale(1.02);
    }
  }
</style>
